import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

/** Versão do /app da organização (docs/features/new-layout-flag, D2). */
export type LayoutVersion = "loading" | "legacy" | "new";

const READ_FAILED = "layout_version_read_failed";

/**
 * Lê `organizations.new_layout_enabled` da organização do usuário (policy de SELECT de membro).
 * Sem organização → legado. Erro de leitura → legado (fail-safe), com aviso no Sentry sem dados da organização.
 * Revalida ao focar a janela para o desligamento valer sem deploy.
 */
export function useLayoutVersionQuery(tenantId: string | null, enabled: boolean): LayoutVersion {
  const orgId = tenantId === null ? null : Number(tenantId);
  const valid = orgId !== null && Number.isSafeInteger(orgId);
  const query = useQuery({
    queryKey: ["layout-version", valid ? orgId : null],
    enabled: enabled && valid,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: "always",
    retry: 1,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from("organizations")
        .select("new_layout_enabled")
        .eq("id", orgId as number)
        .maybeSingle();
      if (error) {
        Sentry.captureMessage(READ_FAILED, { level: "warning" });
        throw new Error(READ_FAILED);
      }
      return data?.new_layout_enabled === true;
    },
  });
  if (!enabled) return "loading";
  if (!valid) return "legacy";
  if (query.isPending) return "loading";
  if (query.isError) return "legacy";
  return query.data ? "new" : "legacy";
}
